import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { getAllowedProfilesForUser } from '../config/inviteAccess';
import { getAllowedProfiles } from '../config/profileAccess';
import { getSharedSiteData } from '../config/albumCatalog';
import SystemIcon from '../components/ui/SystemIcon';
import { CONTACT_LINKS, getSupportMailto } from '../config/contactLinks';
import { flixUi } from '../styles/flixUiTheme';

const TRANSITION_MS = 550;
const sharedSiteData = getSharedSiteData();

function ProfilesPage() {
  const { user, signOutUser } = useAuth();
  const navigate = useNavigate();
  const pendingProfile = sessionStorage.getItem('pendingProfile');
  const inviteMetaRaw = sessionStorage.getItem('pendingInviteMeta');
  const [profiles, setProfiles] = useState([]);
  const [hoveredSlug, setHoveredSlug] = useState('');
  const [isLeaving, setIsLeaving] = useState(false);

  let inviteMeta = null;
  try {
    inviteMeta = inviteMetaRaw ? JSON.parse(inviteMetaRaw) : null;
  } catch (_) {
    inviteMeta = null;
  }

  const selectProfile = useCallback((profileSlug) => {
    if (isLeaving) return;
    setIsLeaving(true);

    window.setTimeout(() => {
      sessionStorage.removeItem('pendingProfile');
      sessionStorage.removeItem('pendingInviteMeta');
      sessionStorage.setItem('selectedProfile', profileSlug);
      navigate(`/v/${profileSlug}`);
    }, TRANSITION_MS - 40);
  }, [isLeaving, navigate]);

  useEffect(() => {
    let alive = true;
    const run = async () => {
      if (!user) {
        if (alive) {
          setProfiles([]);
        }
        return;
      }

      // Fast path: render immediately from local allowlist config.
      const fallbackProfiles = getAllowedProfiles(user);
      if (alive) {
        setProfiles(fallbackProfiles);
      }

      // Firestore hydration runs in background and updates if it has additional matches.
      const nextProfiles = await getAllowedProfilesForUser(user);
      if (!alive) return;
      setProfiles(nextProfiles);
    };

    run();
    return () => {
      alive = false;
    };
  }, [user]);

  useEffect(() => {
    if (!user) return undefined;
    if (!pendingProfile) return undefined;

    const isAllowed = profiles.some((profile) => profile.slug === pendingProfile);
    if (!isAllowed) {
      sessionStorage.removeItem('pendingProfile');
      sessionStorage.removeItem('pendingInviteMeta');
      return undefined;
    }

    const handle = window.setTimeout(() => {
      selectProfile(pendingProfile);
    }, 900);

    return () => window.clearTimeout(handle);
  }, [pendingProfile, profiles, selectProfile, user]);

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return (
    <main
      style={{
        minHeight: '100vh',
        background: flixUi.bg,
        color: flixUi.text,
        fontFamily: flixUi.fontFamily,
        padding: '2rem 1rem 3.25rem',
        display: 'grid',
        placeItems: 'center',
        animation: 'vfFadeIn 620ms ease both',
      }}
    >
      <style>{`
        @keyframes vfFadeIn {
          from { opacity: 0; background-color: #000; }
          to { opacity: 1; background-color: #000; }
        }
        @keyframes vfRiseIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: '#000',
          opacity: isLeaving ? 1 : 0,
          transition: `opacity ${TRANSITION_MS}ms ease`,
          pointerEvents: 'none',
          zIndex: 40,
        }}
      />

      <button
        type="button"
        onClick={() => {
          sessionStorage.removeItem('selectedProfile');
          sessionStorage.removeItem('pendingProfile');
          sessionStorage.removeItem('pendingInviteMeta');
          signOutUser();
        }}
        style={{
          position: 'fixed',
          top: 24,
          right: 24,
          background: 'rgba(255,255,255,0.06)',
          color: 'rgba(255,255,255,0.82)',
          border: `1px solid ${flixUi.borderSubtle}`,
          borderRadius: 999,
          padding: '0.42rem 0.9rem',
          fontSize: '0.82rem',
          letterSpacing: 0.25,
          fontWeight: 500,
          cursor: 'pointer',
          zIndex: 20,
          transition: 'all 180ms ease',
        }}
        onMouseEnter={(event) => {
          event.currentTarget.style.background = 'rgba(255,255,255,0.12)';
          event.currentTarget.style.borderColor = 'rgba(255,255,255,0.45)';
        }}
        onMouseLeave={(event) => {
          event.currentTarget.style.background = 'rgba(255,255,255,0.06)';
          event.currentTarget.style.borderColor = 'rgba(255,255,255,0.24)';
        }}
      >
        Sign out
      </button>
      <img
        src={sharedSiteData?.ui?.footerLogoPath || sharedSiteData?.ui?.logoPath}
        alt="Veeflix"
        style={{
          position: 'fixed',
          top: 18,
          left: 18,
          height: 40,
          width: 'auto',
          objectFit: 'contain',
          zIndex: 20,
          opacity: 0.95,
        }}
      />

      <section style={{ width: '100%', maxWidth: 980, textAlign: 'center', animation: 'vfRiseIn 500ms ease both' }}>
        <h1
          style={{
            fontSize: 'clamp(1.7rem, 5vw, 3.4rem)',
            fontWeight: flixUi.headingWeight,
            letterSpacing: flixUi.titleLetterSpacing,
            lineHeight: 1.06,
            marginBottom: '0.85rem',
          }}
        >
          Choose your
          {' '}
          <span style={{ color: flixUi.primary }}>Veeflix</span>
          {' '}
          Experience
        </h1>

        <p style={{ color: '#a8adb8', marginTop: '0.35rem', fontSize: 'clamp(0.95rem, 1.7vw, 1.1rem)' }}>
          Pick the memory stream you want to watch
        </p>

        {pendingProfile ? (
          <p style={{ color: '#d4d7de', marginTop: '0.95rem', fontSize: '0.98rem' }}>
            Invite accepted.
            {' '}
            {inviteMeta?.from ? `From ${inviteMeta.from},` : ''}
            {' '}
            opening
            {' '}
            <strong>{pendingProfile}</strong>
            ...
          </p>
        ) : null}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 180px))',
            gap: '1.05rem',
            justifyContent: 'center',
            marginTop: '2.15rem',
          }}
        >
          {profiles.map((profile) => {
            const active = hoveredSlug === profile.slug;
            return (
              <button
                key={profile.slug}
                type="button"
                onClick={() => selectProfile(profile.slug)}
                onMouseEnter={() => setHoveredSlug(profile.slug)}
                onMouseLeave={() => setHoveredSlug('')}
                onFocus={() => setHoveredSlug(profile.slug)}
                onBlur={() => setHoveredSlug('')}
                style={{
                  background: 'transparent',
                  border: 0,
                  color: '#fff',
                  cursor: 'pointer',
                  padding: 0,
                  transform: active ? 'scale(1.03)' : 'scale(1)',
                  transition: 'transform 200ms ease',
                }}
              >
                <div
                  style={{
                    borderRadius: 6,
                    overflow: 'hidden',
                    border: active ? '2px solid #fff' : '2px solid rgba(255,255,255,0.2)',
                    boxShadow: active ? '0 10px 34px rgba(0,0,0,0.55)' : '0 6px 18px rgba(0,0,0,0.35)',
                    transition: 'all 220ms ease',
                  }}
                >
                  <img
                    src={profile.avatar}
                    alt={profile.title}
                    style={{ width: '100%', display: 'block', aspectRatio: '1 / 1', objectFit: 'cover' }}
                  />
                </div>
                <strong
                  style={{
                    display: 'block',
                    marginTop: '0.62rem',
                    fontSize: '1.55rem',
                    letterSpacing: -0.4,
                    fontWeight: 600,
                    color: active ? '#fff' : '#8d929d',
                    transition: 'color 220ms ease',
                  }}
                >
                  {profile.title}
                </strong>
                <span
                  style={{
                  color: active ? '#cfd3db' : '#777d88',
                    fontSize: '0.88rem',
                    transition: 'color 220ms ease',
                  }}
                >
                  {profile.subtitle}
                </span>
              </button>
            );
          })}
        </div>

        {profiles.length === 0 ? (
          <>
            <p style={{ marginTop: '1.6rem', color: '#fca5a5' }}>
              No albums are linked to this account yet.
            </p>
            <div style={{ marginTop: '4.2rem' }}>
              <p style={{ margin: 0, color: '#cfd3db', fontSize: '0.95rem' }}>
                Need help accessing your premiere?
              </p>
              <p style={{ marginTop: '0.3rem', marginBottom: 0, color: flixUi.textSubtle, fontSize: '0.9rem' }}>
                <a
                  href={CONTACT_LINKS.dmUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: 'inherit', textDecoration: 'underline', display: 'inline-flex', alignItems: 'center', gap: 5 }}
                >
                  <SystemIcon icon="sparkles" size={12} alt="" tone="muted" />
                  DM us
                </a>
                {' · '}
                <a
                  href={getSupportMailto()}
                  style={{ color: 'inherit', textDecoration: 'underline', display: 'inline-flex', alignItems: 'center', gap: 5 }}
                >
                  <SystemIcon icon="mail" size={12} alt="" tone="muted" />
                  Email support
                </a>
                {' · '}
                <Link to="/" style={{ color: 'inherit', textDecoration: 'underline', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <SystemIcon icon="home" size={12} alt="" tone="muted" />
                  Return Home
                </Link>
              </p>
            </div>
          </>
        ) : null}
      </section>
    </main>
  );
}

export default ProfilesPage;
